import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import { lotService } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import {
  isSuperadminModeEnabled,
  SUPERADMIN_MODE_EVENT,
  SUPERADMIN_MODE_STORAGE_KEY,
} from '../components/SuperadminHeader';
import { setDemoLotActive } from '../components/DemoLotBanner/demoLotMode';

// Define the shape of our lot data
interface LotData {
  lotId: string;
  lotName: string;
  companyName: string;
  address: string;
  lotCapacity: number;
  registryOn: boolean;
  ownerCustomerId: string;
  accountStatus: string;
  isDeleted: boolean;
  // Add other properties as needed
  [key: string]: any;
}

// Define the shape of our context
interface LotContextType {
  lotData: LotData | null;
  loading: boolean;
  error: Error | null;
  isDemoLot: boolean;
  fetchLotData: () => Promise<void>;
  invalidateData: () => void;
}

// Create the context with a default value
const LotContext = createContext<LotContextType | undefined>(undefined);

// Props for LotProvider
interface LotProviderProps {
  children: ReactNode;
  lotId: string | undefined;
}

// Create the provider component
export const LotProvider: React.FC<LotProviderProps> = ({ children, lotId }) => {
  const [lotData, setLotData] = useState<LotData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [invalidationToken, setInvalidationToken] = useState<number>(0);
  const navigate = useNavigate();
  const { user, userLots, fetchUserLots } = useUser();
  const [isSuperadminModeOn, setIsSuperadminModeOn] = useState<boolean>(() =>
    isSuperadminModeEnabled()
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleModeChange = (event: Event) => {
      const detail = (event as CustomEvent<{ enabled: boolean }>).detail;
      setIsSuperadminModeOn(detail.enabled);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === SUPERADMIN_MODE_STORAGE_KEY) {
        setIsSuperadminModeOn(event.newValue === 'true');
      }
    };

    window.addEventListener(SUPERADMIN_MODE_EVENT, handleModeChange as EventListener);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(SUPERADMIN_MODE_EVENT, handleModeChange as EventListener);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const accountRole = (user?.role || '').toLowerCase();
  const isAccountSuperadmin = accountRole === 'superadmin';
  const hasSuperadminOverride = isAccountSuperadmin && isSuperadminModeOn;

  // Track if we've already attempted the access check for this lotId to prevent infinite loops
  const accessCheckAttemptedRef = useRef<string | null>(null);
  // Track component mount status
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reset access check when lotId changes
  useEffect(() => {
    if (lotId !== accessCheckAttemptedRef.current) {
      accessCheckAttemptedRef.current = null;
    }
  }, [lotId]);

  // Check if user has access to this lot
  useEffect(() => {
    // Skip checks if we don't have a lotId
    if (!lotId) return;

    if (hasSuperadminOverride) {
      return;
    }

    // Skip checks during loading or if no user is found
    if (!user) return;

    // Prevent duplicate access checks for the same lotId
    if (accessCheckAttemptedRef.current === lotId) {
      return;
    }

    // If we already have userLots, check access immediately
    if (userLots.length > 0) {
      if (!userLots.some(lot => lot.lotId === lotId)) {
        console.log(`User ${user.userId} attempted to access unauthorized lot ${lotId}`);
        navigate('/login');
      }
      return;
    }

    // Mark that we're attempting the check for this lotId
    accessCheckAttemptedRef.current = lotId;
    console.log('LotContext: userLots is empty, attempting to fetch lots for access check');

    fetchUserLots()
      .then(lots => {
        // Guard against updates if component unmounted
        if (!isMountedRef.current) return;

        console.log('LotContext: Fetched lots for access check, count:', lots.length);
        if (lots.length === 0) {
          console.log(`User ${user.userId} has no lots assigned, redirecting to login`);
          navigate('/login');
        } else if (!lots.some(lot => lot.lotId === lotId)) {
          console.log(`User ${user.userId} attempted to access unauthorized lot ${lotId}`);
          navigate('/login');
        }
      })
      .catch(err => {
        // Guard against updates if component unmounted
        if (!isMountedRef.current) return;

        console.error('LotContext: Error fetching lots for access check:', err);
        navigate('/login');
      });
  }, [lotId, user, userLots, fetchUserLots, navigate, hasSuperadminOverride]);

  // Function to fetch lot data
  const fetchLotData = useCallback(async () => {
    if (!lotId) return;

    setLoading(true);
    setError(null);

    try {
      console.log(`Fetching lot data for ${lotId}`);
      const data = await lotService.getLotById(lotId);

      // Guard against state updates on unmounted component
      if (!isMountedRef.current) return;

      console.log('Lot data fetched successfully:', data);
      setLotData(data);
    } catch (err: any) {
      // Guard against state updates on unmounted component
      if (!isMountedRef.current) return;

      console.error('Error fetching lot data:', err);
      setError(err instanceof Error ? err : new Error('Failed to load lot pricing data. Please try again.'));

      // Redirect on authorization errors
      if (
        !hasSuperadminOverride &&
        err.response &&
        (err.response.status === 403 || err.response.status === 404)
      ) {
        console.log('Unauthorized access or lot not found, redirecting to login');
        navigate('/login');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [lotId, hasSuperadminOverride, navigate]);

  // Function to invalidate data (forces a refetch)
  const invalidateData = () => {
    setInvalidationToken(prev => prev + 1);
  };

  // Fetch lot data when the component mounts or lotId/invalidationToken changes
  useEffect(() => {
    fetchLotData();
  }, [fetchLotData, invalidationToken]);

  const isDemoLot = lotData?.developmentLot === true;

  useEffect(() => {
    setDemoLotActive(isDemoLot);
    return () => {
      setDemoLotActive(false);
    };
  }, [isDemoLot]);

  // Provide the context value
  const contextValue: LotContextType = {
    lotData,
    loading,
    error,
    isDemoLot,
    fetchLotData,
    invalidateData
  };

  return <LotContext.Provider value={contextValue}>{children}</LotContext.Provider>;
};

// Create a custom hook to use the context
export const useLot = (): LotContextType => {
  const context = useContext(LotContext);
  if (context === undefined) {
    throw new Error('useLot must be used within a LotProvider');
  }
  return context;
};
